import React from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { Box, Divider, Typography } from '@mui/material';
import { useIsMobile, mobileBreakpoint, useL10n } from '@uplift/hooks';
import { CancelButton, FormModal, ModalActions, ModalContent, MoneyCents, SubmitButton, useAlert } from '@uplift/ui-v2';

import { useSettingsStyles } from '../../../../settings/styles';
import { useBillingContext } from '../billing.context';
import { PaymentDetailsCard } from '../components/payment-details-card.component';
import { PaymentsPicker } from '../billing-form/PaymentsPicker.component';
import { isApiError, Payment } from '@uplift/api';

export type PayBalanceModalProps = {
  onCancel: () => void;
  onSubmit: (resourceIds: null | string[]) => Promise<void>;
};

export const PayBalanceModal: React.FC<PayBalanceModalProps> = ({ onCancel, onSubmit }) => {
  const alert = useAlert();
  const { paymentsToPay } = useBillingContext();

  return (
    <Formik
      initialValues={{
        payments: paymentsToPay,
      }}
      validationSchema={Yup.object().shape({
        payments: Yup.array().min(1).nullable(),
      })}
      onSubmit={async (values) => {
        try {
          await onSubmit(values.payments?.map(payment => payment.resourceId) ?? null);
        } catch (err) {
          if (isApiError(err)) {
            alert.error(`Unable to pay balance. ${err.response.data.error}`);
          } else {
            alert.error(`Unable to pay balance. ${err.message}`);
          }
        }
      }}
    >
      <PayBalanceModalRaw onCancel={onCancel} onSubmit={onSubmit} />
    </Formik>
  );
};

export const PayBalanceModalRaw: React.FC<PayBalanceModalProps> = ({ onCancel }) => {
  const l10n = useL10n();
  const { classes: settingsClasses } = useSettingsStyles();
  const { payBalanceOnly, balance } = useBillingContext();
  const isMobile = useIsMobile(mobileBreakpoint);
  const formikContext = useFormikContext<{ payments: null | Payment[] }>();

  const actionButtonText = React.useMemo(() => {
    const balanceToPay = formikContext.values.payments?.reduce((acc, payment) => acc + payment.amountCents, 0) ?? balance;

    if (payBalanceOnly) {
      return <span>{l10n.accountSettings.billing.pay} <MoneyCents value={balanceToPay} /></span>;
    }

    return <span>{l10n.accountSettings.billing.turnOnAndPay} <MoneyCents value={balanceToPay} /></span>;
  }, [ formikContext.values.payments, l10n, payBalanceOnly, balance ]);
  const title = payBalanceOnly ? l10n.accountSettings.billing.reviewAndPay : l10n.accountSettings.billing.payBalanceAndTurnOnAutopay;

  return (
    <FormModal
      open
      fullWidth
      fullScreen={isMobile}
      onClose={onCancel}
      title={title}
      maxWidth="md"
      classes={{ paperWidthMd: settingsClasses.paperWidthMd }}
    >
      <Divider />
      <ModalContent dividers={false} noPadding>
        <Box display="flex" flexDirection="column" flexGrow={1} bgcolor={{ xs: 'grey.50', sm: 'transparent' }}>
          {balance > 0 && (
            <Box mt={4} px={{ xs: 0, sm: 3 }}>
              <PaymentsPicker name="payments" />
            </Box>
          )}
          <Box mt={{ xs: 4, sm: 5 }} px={{ xs: 0, sm: 3 }} pb={{ xs: 0, sm: 3 }}>
            <Box px={{ xs: 2, sm: 0 }}>
              <Typography variant="h4">Payment Method</Typography>
            </Box>
            <Box mt={1}>
              <PaymentDetailsCard showAutopay={!payBalanceOnly} />
            </Box>
          </Box>
          <Box display="flex" flexGrow={1} pr={{ xs: 2, sm: 0 }} />
          {isMobile && <Divider />}
        </Box>
      </ModalContent>
      <ModalActions className={isMobile ? settingsClasses.mobileModalActions : null}>
        <CancelButton variant="outlined" onClick={onCancel} fullWidth>
          {l10n.general.cancel}
        </CancelButton>
        <SubmitButton variant="contained" color="secondary" fullWidth>
          {actionButtonText}
        </SubmitButton>
      </ModalActions>
    </FormModal>
  );
};
